// #kinship-quiz-unification — name system for RelationQuizV2.
//
// Two gendered name pools (10 each) backing the puzzle generator's
// "every named character's name must match the gender forced by the
// atom that introduces them" invariant. The validator (below) is run
// as the last step of puzzle generation; if it ever throws, the
// generator has a bug — we'd rather fail loud than silently mis-
// gender a character.
//
// #kinship-quiz-name-length: every entry is ≤6 characters by
// construction. This was previously enforced at the rendering
// layer via `name.slice(0, 6)` in the Avatar, which silently
// truncated longer names — and "Falguni" (female) → "Falgun" (male
// pool) caused a visible gender inversion in the avatar circle
// while the question text + engine answer still treated ego as
// female. Eliminating long names at the source removes the
// truncation step entirely; the Avatar now renders the full name
// verbatim. See the length-cap test below as the regression pin.
//
// Both lists are Ramesh-curated. "Rama" (female-only here) and
// "Kiran" (male-only) are deliberate slot placements despite being
// natively unisex in Telugu — the puzzle generator forces
// character gender by chain atom, so in-quiz gender remains
// correct regardless of the cultural-unisex semantics outside.

export const MALE_NAMES: readonly string[] = [
  'Anand', 'Ramesh', 'Murali', 'Suresh', 'Nikhil',
  'Harsha', 'Manoj',  'Kiran',  'Ramana', 'Vijay',
]

export const FEMALE_NAMES: readonly string[] = [
  'Anitha', 'Swathi', 'Rama',  'Asha',   'Jaya',
  'Sunita', 'Usha',   'Radha', 'Varshi', 'Jyothi',
]

export const UNISEX_NAMES: readonly string[] = []  // not used in v1

export type CharacterGender = 'male' | 'female'

const MALE_NAME_SET   = new Set<string>(MALE_NAMES)
const FEMALE_NAME_SET = new Set<string>(FEMALE_NAMES)

/**
 * The 8-atom quiz alphabet (#kinship-quiz-wife-husband-atoms).
 * Each atom forces the gender of the character it introduces in
 * the chain (Father → male, Mother → female, etc.). The puzzle
 * generator uses this to pick a name from the correct pool; the
 * validator below enforces the rule.
 *
 * Wife/Husband are spouse atoms: Wife always introduces a female
 * character, Husband always introduces a male character. They
 * appear in L4 affinal chains today and L6 chains in the future.
 */
export type QuizAtom =
  | 'Father' | 'Mother' | 'Son' | 'Daughter'
  | 'Brother' | 'Sister' | 'Wife' | 'Husband'

const ATOM_TO_GENDER: Record<QuizAtom, CharacterGender> = {
  Father:   'male',
  Brother:  'male',
  Son:      'male',
  Husband:  'male',
  Mother:   'female',
  Sister:   'female',
  Daughter: 'female',
  Wife:     'female',
}

export function atomForcedGender(atom: QuizAtom): CharacterGender {
  return ATOM_TO_GENDER[atom]
}

/**
 * Pick a random name from the gender's pool, excluding names that
 * are already in use this puzzle. Caller passes a Set; this function
 * does NOT mutate it.
 *
 * Optional rng injection lets tests pin behavior deterministically.
 *
 * #kinship-quiz-pickname-exhausted: when the pool is fully covered
 * by `excludeNames` (every pool entry is excluded), falls back to
 * picking from the full pool with a console.warn rather than
 * throwing. This is a defense-in-depth — the cross-puzzle "recent
 * names" window is also bounded so exhaustion should not occur in
 * practice; if it does, gameplay continues with a non-unique name
 * rather than freezing the Next button on a thrown error.
 */
export function pickName(
  gender: CharacterGender,
  excludeNames: ReadonlySet<string>,
  rng: () => number = Math.random,
): string {
  const pool = gender === 'male' ? MALE_NAMES : FEMALE_NAMES
  const available = pool.filter((n) => !excludeNames.has(n))
  if (available.length === 0) {
    console.warn(
      `pickName: ${gender} pool exhausted (excludeNames=${Array.from(excludeNames).join(',')}), falling back to full pool`,
    )
    return pool[Math.floor(rng() * pool.length)]
  }
  return available[Math.floor(rng() * available.length)]
}

/**
 * Per-character record carried inside a puzzle. The `atom` is the
 * chain step that introduced this character; the `gender` was set
 * to atomForcedGender(atom) at generation time; the `name` was
 * picked from the matching pool. Validator below confirms all
 * three remain consistent.
 */
export interface PuzzleCharacter {
  name: string
  gender: CharacterGender
  atom: QuizAtom
}

export interface QuizPuzzleShape {
  egoName: string
  egoGender: CharacterGender
  chain: QuizAtom[]
  characters: PuzzleCharacter[]   // length === chain.length
}

/**
 * Last-step generation check: every character's name must live in
 * the pool matching its (forced) gender, and that gender must
 * match what the atom would force. Throws on any mismatch — do
 * NOT silently retry, since a mismatch indicates a generator bug
 * worth investigating.
 *
 * Ego's name must also be in a gendered pool matching egoGender.
 * Unisex names are NOT accepted in v1 (they'd defeat the visual
 * gender cue the quiz uses to teach ego-gender dependency).
 */
export function validateNameGenderConsistency(puzzle: QuizPuzzleShape): void {
  if (puzzle.characters.length !== puzzle.chain.length) {
    throw new Error(
      `validateNameGenderConsistency: characters.length (${puzzle.characters.length}) ` +
      `!== chain.length (${puzzle.chain.length})`,
    )
  }
  assertNameMatchesGender('ego', puzzle.egoName, puzzle.egoGender)
  for (let i = 0; i < puzzle.chain.length; i++) {
    const atom = puzzle.chain[i]
    const ch   = puzzle.characters[i]
    const forced = atomForcedGender(atom)
    if (ch.atom !== atom) {
      throw new Error(
        `validateNameGenderConsistency: character[${i}].atom="${ch.atom}" ` +
        `!== chain[${i}]="${atom}"`,
      )
    }
    if (ch.gender !== forced) {
      throw new Error(
        `validateNameGenderConsistency: character[${i}].gender="${ch.gender}" ` +
        `!= forced "${forced}" by atom "${atom}"`,
      )
    }
    assertNameMatchesGender(`character[${i}] (${atom})`, ch.name, forced)
  }
}

function assertNameMatchesGender(role: string, name: string, gender: CharacterGender): void {
  const pool = gender === 'male' ? MALE_NAME_SET : FEMALE_NAME_SET
  if (!pool.has(name)) {
    throw new Error(
      `validateNameGenderConsistency: ${role} name "${name}" not in ${gender.toUpperCase()}_NAMES pool`,
    )
  }
}
